<script lang="ts">
  import { onMount, type ComponentType } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { userSettings } from '../lib/stores/settingsStore';
  import { householdsStore } from '../lib/stores/householdsStore';
  import { getSupportedLocales } from '../lib/i18n';
  import { memberRoleLabelKey } from '../lib/memberRoles';
  import HouseholdManagement from '../lib/components/HouseholdManagement.svelte';
  import type { HouseholdMember } from '../lib/api';
  import {
    Settings2,
    Globe,
    Calendar,
    Clock,
    Users,
    UserPlus,
    Pencil,
    Trash2,
    Check,
    X,
    Home,
    BarChart3,
    Info,
    Mail,
    Link2,
    Plus,
    Shield,
    Download,
    AlertTriangle,
    Share2,
    Eye,
    EyeOff,
    HardDrive,
  } from 'lucide-svelte';
  import { navigate } from 'svelte-routing';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import {
    Button,
    Card,
    Badge,
    Modal,
    Spinner,
    SectionHeading,
    CopyIconButton,
  } from '../lib/components/ui/index';
  import { toastStore } from '../lib/stores/toastStore';
  import { api } from '../lib/api';
  import type {
    IngestEmailAddressResponse,
    ShareLinkOverviewDto,
    NgxIntegrationStatusResponse,
    FileSyncIntegrationStatusResponse,
  } from '../lib/api';

  // Tab state
  type Tab = 'preferences' | 'members' | 'households' | 'interfaces' | 'privacy' | 'sharing';
  let activeTab: Tab = 'preferences';

  const tabs: { id: Tab | 'usage'; icon: ComponentType; labelKey: string }[] = [
    { id: 'preferences', icon: Settings2, labelKey: 'pages.settings.tab_preferences' },
    { id: 'members', icon: Users, labelKey: 'pages.settings.tab_members' },
    { id: 'households', icon: Home, labelKey: 'pages.settings.tab_households' },
    { id: 'interfaces', icon: Link2, labelKey: 'pages.settings.tab_interfaces' },
    { id: 'sharing', icon: Share2, labelKey: 'pages.settings.tab_sharing' },
    { id: 'privacy', icon: Shield, labelKey: 'pages.settings.tab_privacy' },
    { id: 'usage', icon: BarChart3, labelKey: 'pages.settings.tab_usage' },
  ];

  // Subscribe to default household (with members)
  const defaultHousehold = householdsStore.defaultHousehold;
  import {
    availableLocales,
    availableDateFormats,
    availableTimeFormats,
    formatDate,
    type DateFormatType,
    type TimeFormatType,
  } from '../lib/settings';

  // User settings state
  let locale: string;
  let dateFormat: DateFormatType;
  let timeFormat: TimeFormatType;
  let saving = false;
  let error: string | null = null;

  // Household members state
  let membersLoading = false;
  let membersError: string | null = null;
  let newMemberName = '';
  let newMemberRole = '';
  let editingMember: HouseholdMember | null = null;
  let editName = '';
  let editRole = '';
  let deleteMemberTarget: HouseholdMember | null = null;

  // Share Links state (Spec 014 E8)
  let shareLinks: ShareLinkOverviewDto[] = [];
  let shareLinksLoading = false;
  let shareLinksError: string | null = null;
  let copiedShareLinkId: string | null = null;
  let revokingShareLinkId: string | null = null;

  async function loadShareLinks() {
    shareLinksLoading = true;
    shareLinksError = null;
    try {
      shareLinks = await api.sharing.listAllShareLinks();
    } catch {
      shareLinksError = $_('pages.settings.share_links_error');
    } finally {
      shareLinksLoading = false;
    }
  }

  async function handleCopyShareLink(token: string, linkId: string) {
    const url = api.sharing.getSharedDownloadUrl(token);
    await navigator.clipboard.writeText(url);
    copiedShareLinkId = linkId;
    setTimeout(() => {
      copiedShareLinkId = null;
    }, 2000);
  }

  async function handleRevokeShareLink(documentId: string, linkId: string) {
    revokingShareLinkId = linkId;
    try {
      await api.sharing.revokeShareLink(documentId, linkId);
      shareLinks = shareLinks.filter((l) => l.id !== linkId);
    } catch {
      shareLinksError = $_('pages.settings.share_links_error');
    } finally {
      revokingShareLinkId = null;
    }
  }

  // Email Ingest state
  let ingestEmail: IngestEmailAddressResponse | null = null;
  let ingestLoading = true;
  let ingestCopied = false;

  // Allowed senders state
  let newSenderEmail = '';
  let senderSaving = false;
  let senderError: string | null = null;

  async function loadIngestEmail() {
    try {
      ingestEmail = await api.settings.getIngestEmailAddress();
    } catch (err) {
      console.error('Could not load ingest email:', err);
      ingestEmail = { enabled: false, emailAddress: null };
    } finally {
      ingestLoading = false;
    }
  }

  async function copyIngestEmail() {
    if (!ingestEmail?.emailAddress) return;
    await navigator.clipboard.writeText(ingestEmail.emailAddress);
    ingestCopied = true;
    setTimeout(() => (ingestCopied = false), 2000);
  }

  async function addAllowedSender() {
    const email = newSenderEmail.trim().toLowerCase();
    if (!email) return;
    if ($userSettings.allowedIngestEmails.includes(email)) {
      senderError = $_('pages.settings.sender_duplicate');
      return;
    }
    senderSaving = true;
    senderError = null;
    try {
      const updated = [...$userSettings.allowedIngestEmails, email];
      await userSettings.updateSettings({ allowedIngestEmails: updated });
      newSenderEmail = '';
      toastStore.success($_('pages.settings.success'));
    } catch {
      senderError = $_('pages.settings.sender_add_error');
    } finally {
      senderSaving = false;
    }
  }

  // ngx-Integration state (Spec 037 E1/E4/E5)
  let ngxStatus: NgxIntegrationStatusResponse | null = null;
  let ngxLoading = true;
  let ngxError = false;
  let ngxToggleError = false;
  let ngxToggling = false;
  let ngxPasswordRevealed = false;
  let ngxCopiedField: 'baseUrl' | 'username' | 'password' | null = null;

  async function loadNgxIntegrationStatus() {
    ngxLoading = true;
    ngxError = false;
    try {
      ngxStatus = await api.integrations.getNgxIntegrationStatus();
    } catch (err) {
      console.error('Could not load ngx-integration status:', err);
      ngxError = true;
    } finally {
      ngxLoading = false;
    }
  }

  async function toggleNgxIntegration() {
    if (!ngxStatus || ngxToggling) return;
    ngxToggling = true;
    ngxToggleError = false;
    try {
      ngxStatus = await api.integrations.updateNgxIntegrationStatus(!ngxStatus.enabled);
    } catch (err) {
      console.error('Could not update ngx-integration status:', err);
      ngxToggleError = true;
    } finally {
      ngxToggling = false;
    }
  }

  async function copyNgxField(field: 'baseUrl' | 'username' | 'password', value: string) {
    await navigator.clipboard.writeText(value);
    ngxCopiedField = field;
    setTimeout(() => {
      ngxCopiedField = null;
    }, 2000);
  }

  // Windows-Drive-Integration state (Spec 041 E1/E4/E5)
  let filesyncStatus: FileSyncIntegrationStatusResponse | null = null;
  let filesyncLoading = true;
  let filesyncError = false;
  let filesyncToggleError = false;
  let filesyncToggling = false;
  let filesyncPasswordRevealed = false;
  let filesyncCopiedField: 'syncUrl' | 'username' | 'password' | null = null;

  async function loadFileSyncIntegrationStatus() {
    filesyncLoading = true;
    filesyncError = false;
    try {
      filesyncStatus = await api.integrations.getFileSyncIntegrationStatus();
    } catch (err) {
      console.error('Could not load filesync-integration status:', err);
      filesyncError = true;
    } finally {
      filesyncLoading = false;
    }
  }

  async function toggleFileSyncIntegration() {
    if (!filesyncStatus || filesyncToggling) return;
    filesyncToggling = true;
    filesyncToggleError = false;
    try {
      filesyncStatus = await api.integrations.updateFileSyncIntegrationStatus(
        !filesyncStatus.enabled,
      );
    } catch (err) {
      console.error('Could not update filesync-integration status:', err);
      filesyncToggleError = true;
    } finally {
      filesyncToggling = false;
    }
  }

  async function copyFilesyncField(field: 'syncUrl' | 'username' | 'password', value: string) {
    await navigator.clipboard.writeText(value);
    filesyncCopiedField = field;
    setTimeout(() => {
      filesyncCopiedField = null;
    }, 2000);
  }

  async function removeAllowedSender(email: string) {
    senderSaving = true;
    senderError = null;
    try {
      const updated = $userSettings.allowedIngestEmails.filter((e) => e !== email);
      await userSettings.updateSettings({ allowedIngestEmails: updated });
      toastStore.success($_('pages.settings.success'));
    } catch {
      senderError = $_('pages.settings.sender_remove_error');
    } finally {
      senderSaving = false;
    }
  }

  // Privacy / DSGVO state
  let exportLoading = false;
  let exportError: string | null = null;
  let deleteLoading = false;
  let deleteError: string | null = null;
  let showDeleteModal = false;
  let deleteConfirmText = '';
  let includeHouseholds = false;

  async function handleExportData() {
    exportLoading = true;
    exportError = null;
    try {
      const blob = await api.settings.exportUserData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `datenexport_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toastStore.success($_('pages.settings.privacy_export_success'), 5000);
    } catch {
      exportError = $_('pages.settings.privacy_export_error');
    } finally {
      exportLoading = false;
    }
  }

  async function handleDeleteData() {
    if (deleteConfirmText !== $_('pages.settings.privacy_delete_confirm_word')) return;
    deleteLoading = true;
    deleteError = null;
    try {
      await api.settings.deleteUserData(includeHouseholds);
      showDeleteModal = false;
      // Clear auth and redirect to login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    } catch {
      deleteError = $_('pages.settings.privacy_delete_error');
    } finally {
      deleteLoading = false;
    }
  }

  // Available roles for members
  const availableRoles = [
    { value: '', labelKey: 'pages.settings.role_none' },
    { value: 'Parent', labelKey: 'pages.settings.role_parent' },
    { value: 'Child', labelKey: 'pages.settings.role_child' },
    { value: 'Other', labelKey: 'pages.settings.role_other' },
  ];

  // Localize a stored member role for display (#241); raw value for custom roles.
  function roleLabel(role: string): string {
    const key = memberRoleLabelKey(role);
    return key ? $_(key) : role;
  }

  // Subscribe to settings store
  let currentLocale: string;
  userSettings.subscribe((settings) => {
    locale = settings.locale;
    currentLocale = settings.locale;
    dateFormat = settings.dateFormat;
    timeFormat = settings.timeFormat;
  });

  async function handleSave() {
    saving = true;
    error = null;

    try {
      if (locale !== currentLocale) {
        await userSettings.updateLocale(locale);
        currentLocale = locale;
      }
      if (dateFormat !== $userSettings.dateFormat || timeFormat !== $userSettings.timeFormat) {
        await userSettings.updateSettings({ dateFormat, timeFormat });
      }
      toastStore.success($_('pages.settings.success'));
    } catch {
      error = $_('pages.settings.error');
    } finally {
      saving = false;
    }
  }

  // Household member functions
  async function handleAddMember() {
    if (!newMemberName.trim()) return;
    membersLoading = true;
    membersError = null;

    try {
      await householdsStore.addMember(newMemberName.trim(), newMemberRole || undefined);
      newMemberName = '';
      newMemberRole = '';
      toastStore.success($_('pages.settings.members_success'));
    } catch {
      membersError = $_('pages.settings.member_add_error');
    } finally {
      membersLoading = false;
    }
  }

  function startEdit(member: HouseholdMember) {
    editingMember = member;
    editName = member.name;
    editRole = member.role || '';
  }

  function cancelEdit() {
    editingMember = null;
    editName = '';
    editRole = '';
  }

  async function handleUpdateMember() {
    if (!editingMember || !editName.trim()) return;
    membersLoading = true;
    membersError = null;

    try {
      await householdsStore.updateMember(editingMember.id, editName.trim(), editRole || undefined);
      cancelEdit();
      toastStore.success($_('pages.settings.members_success'));
    } catch {
      membersError = $_('pages.settings.member_update_error');
    } finally {
      membersLoading = false;
    }
  }

  async function handleRemoveMember() {
    if (!deleteMemberTarget) return;
    membersLoading = true;
    membersError = null;

    try {
      await householdsStore.removeMember(deleteMemberTarget.id);
      deleteMemberTarget = null;
      toastStore.success($_('pages.settings.members_success'));
    } catch {
      membersError = $_('pages.settings.member_remove_error');
    } finally {
      membersLoading = false;
    }
  }

  function handleTabChange(tab: Tab | 'usage') {
    if (tab === 'usage') {
      navigate('/settings/usage');
      return;
    }
    activeTab = tab as Tab;
    if (tab === 'sharing' && shareLinks.length === 0 && !shareLinksLoading) {
      loadShareLinks();
    }
  }

  onMount(async () => {
    if (!userSettings.isLoaded) {
      await userSettings.load();
    }
    if (!householdsStore.isDefaultHouseholdLoaded) {
      try {
        await householdsStore.loadDefaultHousehold();
      } catch (err) {
        console.error('Could not load household:', err);
      }
    }
    loadIngestEmail();
    loadNgxIntegrationStatus();
    loadFileSyncIntegrationStatus();
  });
</script>

<PageHeader title={$_('pages.settings.title')} />

<!-- Tab Navigation -->
<div class="flex gap-1 border-b border-border mb-6">
  {#each tabs as tab (tab.id)}
    <button
      data-testid="settings-tab-{tab.id}"
      on:click={() => handleTabChange(tab.id)}
      class="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px
        {activeTab === tab.id
        ? 'border-accent text-accent'
        : 'border-transparent text-text-secondary hover:text-text hover:border-border-strong'}"
    >
      <svelte:component this={tab.icon} class="w-4 h-4" strokeWidth={1.5} />
      {$_(tab.labelKey)}
    </button>
  {/each}
</div>

<div class="max-w-2xl space-y-6">
  <!-- Tab: Preferences -->
  {#if activeTab === 'preferences'}
    <Card>
      <form on:submit|preventDefault={handleSave} class="flex flex-col gap-4">
        <!-- Locale -->
        <div>
          <label for="locale" class="block mb-1.5">
            <span class="flex items-center gap-1.5 text-sm font-medium text-text">
              <Globe class="w-3.5 h-3.5 text-text-muted" strokeWidth={1.5} />
              {$_('pages.settings.language')}
            </span>
            <span class="text-xs text-text-muted mt-0.5 block"
              >{$_('pages.settings.language_help')}</span
            >
          </label>
          <select
            id="locale"
            bind:value={locale}
            class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
          >
            {#each availableLocales as loc (loc.value)}
              <option value={loc.value}>{loc.label}</option>
            {/each}
          </select>
        </div>

        <!-- Date Format -->
        <div>
          <label for="dateFormat" class="block mb-1.5">
            <span class="flex items-center gap-1.5 text-sm font-medium text-text">
              <Calendar class="w-3.5 h-3.5 text-text-muted" strokeWidth={1.5} />
              {$_('pages.settings.date_format')}
            </span>
            <span class="text-xs text-text-muted mt-0.5 block"
              >{$_('pages.settings.date_format_help')}</span
            >
          </label>
          <select
            id="dateFormat"
            bind:value={dateFormat}
            class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
          >
            {#each availableDateFormats as fmt (fmt.value)}
              <option value={fmt.value}>{fmt.label}</option>
            {/each}
          </select>
        </div>

        <!-- Time Format -->
        <div>
          <label for="timeFormat" class="block mb-1.5">
            <span class="flex items-center gap-1.5 text-sm font-medium text-text">
              <Clock class="w-3.5 h-3.5 text-text-muted" strokeWidth={1.5} />
              {$_('pages.settings.time_format')}
            </span>
            <span class="text-xs text-text-muted mt-0.5 block"
              >{$_('pages.settings.time_format_help')}</span
            >
          </label>
          <select
            id="timeFormat"
            bind:value={timeFormat}
            class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
          >
            {#each availableTimeFormats as fmt (fmt.value)}
              <option value={fmt.value}>{fmt.label}</option>
            {/each}
          </select>
        </div>

        <!-- Messages -->
        {#if error}
          <div class="px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error">
            {error}
          </div>
        {/if}

        <!-- Save -->
        <div class="flex justify-end">
          <Button variant="primary" type="submit" loading={saving} disabled={saving}>
            {#if saving}
              {$_('pages.settings.saving')}
            {:else}
              {$_('pages.settings.save')}
            {/if}
          </Button>
        </div>
      </form>
    </Card>
  {/if}

  <!-- Tab: Household Members -->
  {#if activeTab === 'members'}
    <Card>
      <SectionHeading icon={Users} title={$_('pages.settings.tab_members')} />
      <p class="text-xs text-text-muted mb-4">{$_('pages.settings.households_description')}</p>

      {#if $defaultHousehold}
        <div class="flex flex-col gap-2 mb-4">
          {#each $defaultHousehold.members as member (member.id)}
            <div class="flex justify-between items-center px-3 py-2.5 bg-bg-alt rounded">
              {#if editingMember?.id === member.id}
                <div class="flex flex-wrap gap-2 w-full items-center">
                  <input
                    type="text"
                    bind:value={editName}
                    placeholder="Name"
                    class="flex-1 min-w-[120px] px-3 py-1.5 bg-surface border border-border rounded text-sm text-text focus:outline-none focus:border-accent"
                  />
                  <select
                    bind:value={editRole}
                    class="px-3 py-1.5 bg-surface border border-border rounded text-sm text-text"
                  >
                    {#each availableRoles as role (role.value)}
                      <option value={role.value}>{$_(role.labelKey)}</option>
                    {/each}
                  </select>
                  <div class="flex gap-1.5">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={membersLoading || !editName.trim()}
                      on:click={handleUpdateMember}
                    >
                      <Check class="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={membersLoading}
                      on:click={cancelEdit}
                    >
                      <X class="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              {:else}
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-text">{member.name}</span>
                  {#if member.role}
                    <Badge variant="default">{roleLabel(member.role)}</Badge>
                  {/if}
                </div>
                <div class="flex gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={membersLoading}
                    on:click={() => startEdit(member)}
                  >
                    <Pencil class="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    iconOnly
                    disabled={membersLoading}
                    on:click={() => (deleteMemberTarget = member)}
                  >
                    <Trash2 class="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              {/if}
            </div>
          {:else}
            <div class="p-4 bg-bg-alt/50 border border-border/50 rounded-lg text-center">
              <div
                class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent/10 mb-3 text-accent"
              >
                <Info class="w-5 h-5" strokeWidth={1.5} />
              </div>
              <h4 class="text-sm font-semibold text-text mb-1">
                {$_('pages.settings.no_members_onboarding_title')}
              </h4>
              <p class="text-xs text-text-muted max-w-sm mx-auto">
                {$_('pages.settings.no_members_onboarding_desc')}
              </p>
            </div>
          {/each}
        </div>
      {:else}
        <p class="text-sm text-text-muted">{$_('pages.settings.loading_members')}</p>
      {/if}

      <!-- Add Member Form -->
      <div class="pt-3 border-t border-border">
        <h3 class="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
          {$_('pages.settings.add_household_member')}
        </h3>
        <div class="flex gap-2 flex-wrap">
          <input
            type="text"
            bind:value={newMemberName}
            placeholder={$_('pages.settings.add_household_member_name')}
            class="flex-1 min-w-[150px] px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
          />
          <select
            bind:value={newMemberRole}
            class="px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text"
          >
            {#each availableRoles as role (role.value)}
              <option value={role.value}>{$_(role.labelKey)}</option>
            {/each}
          </select>
          <Button
            variant="primary"
            size="sm"
            disabled={membersLoading || !newMemberName.trim()}
            on:click={handleAddMember}
          >
            <UserPlus class="w-4 h-4" strokeWidth={1.5} />
            {#if membersLoading}
              {$_('pages.settings.adding')}
            {:else}
              {$_('pages.settings.add_household_member')}
            {/if}
          </Button>
        </div>
      </div>

      {#if membersError}
        <div class="mt-3 px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error">
          {membersError}
        </div>
      {/if}
    </Card>
  {/if}

  <!-- Tab: Household Management -->
  {#if activeTab === 'households'}
    <Card>
      <HouseholdManagement />
    </Card>
  {/if}

  <!-- Tab: Interfaces -->
  {#if activeTab === 'interfaces'}
    <!-- ngx-Integration activation (Spec 037 E5) -->
    <Card>
      <SectionHeading icon={Link2} title={$_('pages.settings.ngx_integration_title')} />
      <p class="text-xs text-text-muted mb-3">{$_('pages.settings.ngx_integration_desc')}</p>

      {#if ngxLoading}
        <div
          class="flex items-center gap-2 text-sm text-text-muted"
          data-testid="settings-ngx-loading"
          role="status"
        >
          <Spinner size="sm" decorative />
          {$_('pages.settings.ngx_integration_loading')}
        </div>
      {:else}
        <div class="flex items-center justify-between">
          <span class="text-sm text-text">{$_('pages.settings.ngx_integration_toggle_label')}</span>
          <button
            type="button"
            role="switch"
            aria-checked={ngxStatus?.enabled ?? false}
            aria-label={$_('pages.settings.ngx_integration_toggle_label')}
            disabled={ngxToggling}
            data-testid="settings-ngx-toggle"
            on:click={toggleNgxIntegration}
            class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50
              {ngxStatus?.enabled ? 'bg-accent' : 'bg-bg-alt border border-border'}"
          >
            <span
              class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                {ngxStatus?.enabled ? 'translate-x-6' : 'translate-x-1'}"
            ></span>
          </button>
        </div>
        <p class="text-xs text-text-muted mt-1">
          {ngxStatus?.enabled
            ? $_('pages.settings.ngx_integration_enabled')
            : $_('pages.settings.ngx_integration_disabled')}
        </p>

        {#if ngxError || ngxToggleError}
          <div
            class="mt-3 px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error"
            data-testid="settings-ngx-error"
          >
            {ngxToggleError
              ? $_('pages.settings.ngx_integration_toggle_error')
              : $_('pages.settings.ngx_integration_error')}
          </div>
        {/if}

        {#if ngxStatus && ngxStatus.enabled}
          <div class="flex flex-col gap-3 mt-3 pt-3 border-t border-border">
            <!-- Base URL -->
            <div>
              <span
                id="settings-ngx-baseurl-label"
                class="block text-xs font-medium text-text-muted mb-1"
                >{$_('pages.settings.ngx_integration_base_url')}</span
              >
              <div class="flex items-center gap-2">
                <code
                  class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text font-mono select-all"
                  data-testid="settings-ngx-baseurl"
                >
                  {ngxStatus.baseUrl}
                </code>
                <CopyIconButton
                  data-testid="settings-ngx-copy-baseurl"
                  aria-describedby="settings-ngx-baseurl-label"
                  copied={ngxCopiedField === 'baseUrl'}
                  label={$_('pages.settings.ngx_integration_copy')}
                  copiedLabel={$_('pages.settings.ngx_integration_copied')}
                  on:click={() => copyNgxField('baseUrl', ngxStatus?.baseUrl ?? '')}
                />
              </div>
            </div>

            <!-- Username -->
            <div>
              <span
                id="settings-ngx-username-label"
                class="block text-xs font-medium text-text-muted mb-1"
                >{$_('pages.settings.ngx_integration_username')}</span
              >
              <div class="flex items-center gap-2">
                <code
                  class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text font-mono select-all"
                  data-testid="settings-ngx-username"
                >
                  {ngxStatus.username}
                </code>
                <CopyIconButton
                  data-testid="settings-ngx-copy-username"
                  aria-describedby="settings-ngx-username-label"
                  copied={ngxCopiedField === 'username'}
                  label={$_('pages.settings.ngx_integration_copy')}
                  copiedLabel={$_('pages.settings.ngx_integration_copied')}
                  on:click={() => copyNgxField('username', ngxStatus?.username ?? '')}
                />
              </div>
            </div>

            <!-- Password (masked, revealable — E5) -->
            <div>
              <span
                id="settings-ngx-password-label"
                class="block text-xs font-medium text-text-muted mb-1"
                >{$_('pages.settings.ngx_integration_password')}</span
              >
              <div class="flex items-center gap-2">
                <code
                  class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text font-mono select-all"
                  data-testid="settings-ngx-password"
                >
                  {ngxPasswordRevealed ? ngxStatus.password : '••••••••'}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  iconOnly
                  data-testid="settings-ngx-password-reveal"
                  aria-label={ngxPasswordRevealed
                    ? $_('pages.settings.ngx_integration_hide_password')
                    : $_('pages.settings.ngx_integration_reveal_password')}
                  on:click={() => (ngxPasswordRevealed = !ngxPasswordRevealed)}
                >
                  <svelte:component
                    this={ngxPasswordRevealed ? EyeOff : Eye}
                    class="w-4 h-4"
                    strokeWidth={1.5}
                  />
                </Button>
                <CopyIconButton
                  data-testid="settings-ngx-copy-password"
                  aria-describedby="settings-ngx-password-label"
                  copied={ngxCopiedField === 'password'}
                  label={$_('pages.settings.ngx_integration_copy')}
                  copiedLabel={$_('pages.settings.ngx_integration_copied')}
                  on:click={() => copyNgxField('password', ngxStatus?.password ?? '')}
                />
              </div>
            </div>
          </div>
        {/if}
      {/if}
    </Card>

    <!-- Windows-Drive-Integration activation (Spec 041 E5/E5a) -->
    <!-- The wrapper only exists to carry a card-level data-testid: Card.svelte does not forward
         $$restProps to its root element, so data-testid on <Card> never reaches the DOM. The E5a
         tests need a card-level selector to assert the icon differs from the ngx card and that this
         card sits between the ngx and email-ingest cards. Widening Card.svelte would touch every
         view — deliberately not done here. -->
    <div data-testid="settings-filesync-card">
      <Card>
        <SectionHeading icon={HardDrive} title={$_('pages.settings.filesync_integration_title')} />
        <p class="text-xs text-text-muted mb-3">{$_('pages.settings.filesync_integration_desc')}</p>

        {#if filesyncLoading}
          <div
            class="flex items-center gap-2 text-sm text-text-muted"
            data-testid="settings-filesync-loading"
            role="status"
          >
            <Spinner size="sm" decorative />
            {$_('pages.settings.filesync_integration_loading')}
          </div>
        {:else}
          <div class="flex items-center justify-between">
            <span class="text-sm text-text"
              >{$_('pages.settings.filesync_integration_toggle_label')}</span
            >
            <button
              type="button"
              role="switch"
              aria-checked={filesyncStatus?.enabled ?? false}
              aria-label={$_('pages.settings.filesync_integration_toggle_label')}
              disabled={filesyncToggling}
              data-testid="settings-filesync-toggle"
              on:click={toggleFileSyncIntegration}
              class="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50
              {filesyncStatus?.enabled ? 'bg-accent' : 'bg-bg-alt border border-border'}"
            >
              <span
                class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                {filesyncStatus?.enabled ? 'translate-x-6' : 'translate-x-1'}"
              ></span>
            </button>
          </div>
          <p class="text-xs text-text-muted mt-1">
            {filesyncStatus?.enabled
              ? $_('pages.settings.filesync_integration_enabled')
              : $_('pages.settings.filesync_integration_disabled')}
          </p>

          {#if filesyncError || filesyncToggleError}
            <div
              class="mt-3 px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error"
              data-testid="settings-filesync-error"
            >
              {filesyncToggleError
                ? $_('pages.settings.filesync_integration_toggle_error')
                : $_('pages.settings.filesync_integration_error')}
            </div>
          {/if}

          {#if filesyncStatus && filesyncStatus.enabled}
            <div class="flex flex-col gap-3 mt-3 pt-3 border-t border-border">
              <!-- Sync URL -->
              <div>
                <span
                  id="settings-filesync-syncurl-label"
                  class="block text-xs font-medium text-text-muted mb-1"
                  >{$_('pages.settings.filesync_integration_sync_url')}</span
                >
                <div class="flex items-center gap-2">
                  <code
                    class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text font-mono select-all"
                    data-testid="settings-filesync-syncurl"
                  >
                    {filesyncStatus.syncUrl}
                  </code>
                  <CopyIconButton
                    data-testid="settings-filesync-copy-syncurl"
                    aria-describedby="settings-filesync-syncurl-label"
                    copied={filesyncCopiedField === 'syncUrl'}
                    label={$_('pages.settings.filesync_integration_copy')}
                    copiedLabel={$_('pages.settings.filesync_integration_copied')}
                    on:click={() => copyFilesyncField('syncUrl', filesyncStatus?.syncUrl ?? '')}
                  />
                </div>
              </div>

              <!-- Username -->
              <div>
                <span
                  id="settings-filesync-username-label"
                  class="block text-xs font-medium text-text-muted mb-1"
                  >{$_('pages.settings.filesync_integration_username')}</span
                >
                <div class="flex items-center gap-2">
                  <code
                    class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text font-mono select-all"
                    data-testid="settings-filesync-username"
                  >
                    {filesyncStatus.username}
                  </code>
                  <CopyIconButton
                    data-testid="settings-filesync-copy-username"
                    aria-describedby="settings-filesync-username-label"
                    copied={filesyncCopiedField === 'username'}
                    label={$_('pages.settings.filesync_integration_copy')}
                    copiedLabel={$_('pages.settings.filesync_integration_copied')}
                    on:click={() => copyFilesyncField('username', filesyncStatus?.username ?? '')}
                  />
                </div>
              </div>

              <!-- Password (masked, revealable — E5) -->
              <div>
                <span
                  id="settings-filesync-password-label"
                  class="block text-xs font-medium text-text-muted mb-1"
                  >{$_('pages.settings.filesync_integration_password')}</span
                >
                <div class="flex items-center gap-2">
                  <code
                    class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text font-mono select-all"
                    data-testid="settings-filesync-password"
                  >
                    {filesyncPasswordRevealed ? filesyncStatus.password : '••••••••'}
                  </code>
                  <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    data-testid="settings-filesync-password-reveal"
                    aria-label={filesyncPasswordRevealed
                      ? $_('pages.settings.filesync_integration_hide_password')
                      : $_('pages.settings.filesync_integration_reveal_password')}
                    on:click={() => (filesyncPasswordRevealed = !filesyncPasswordRevealed)}
                  >
                    <svelte:component
                      this={filesyncPasswordRevealed ? EyeOff : Eye}
                      class="w-4 h-4"
                      strokeWidth={1.5}
                    />
                  </Button>
                  <CopyIconButton
                    data-testid="settings-filesync-copy-password"
                    aria-describedby="settings-filesync-password-label"
                    copied={filesyncCopiedField === 'password'}
                    label={$_('pages.settings.filesync_integration_copy')}
                    copiedLabel={$_('pages.settings.filesync_integration_copied')}
                    on:click={() => copyFilesyncField('password', filesyncStatus?.password ?? '')}
                  />
                </div>
              </div>

              <!-- Download button (E4) + SmartScreen hint directly below it (E5a.2) -->
              <div>
                <a
                  href={filesyncStatus.clientDownloadUrl}
                  data-testid="settings-filesync-download"
                  class="inline-flex items-center gap-2 px-3 py-2 bg-accent text-on-accent rounded text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <Download class="w-4 h-4" strokeWidth={1.5} />
                  {$_('pages.settings.filesync_integration_download_button')}
                </a>
                <p
                  class="flex items-start gap-1.5 text-xs text-text-muted mt-2"
                  data-testid="settings-filesync-smartscreen-note"
                >
                  <AlertTriangle class="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={1.5} />
                  {$_('pages.settings.filesync_integration_smartscreen_note')}
                </p>
              </div>
            </div>
          {/if}
        {/if}
      </Card>
    </div>

    <!-- Email Ingest Address -->
    <Card>
      <SectionHeading icon={Mail} title={$_('pages.settings.interfaces_email_title')} />
      <p class="text-xs text-text-muted mb-3">{$_('pages.settings.interfaces_email_desc')}</p>

      {#if ingestLoading}
        <div class="flex items-center gap-2 text-sm text-text-muted">
          <Spinner size="sm" />
          {$_('pages.settings.email_ingest_loading')}
        </div>
      {:else if ingestEmail?.enabled && ingestEmail?.emailAddress}
        <div>
          <span class="block text-xs font-medium text-text-muted mb-1"
            >{$_('pages.settings.email_ingest_address')}</span
          >
          <div class="flex items-center gap-2">
            <code
              class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text font-mono select-all"
            >
              {ingestEmail.emailAddress}
            </code>
            <CopyIconButton
              copied={ingestCopied}
              label={$_('pages.settings.email_ingest_copy')}
              copiedLabel={$_('pages.settings.email_ingest_copied')}
              on:click={copyIngestEmail}
            />
          </div>
        </div>
      {:else}
        <p class="text-sm text-text-muted">{$_('pages.settings.email_ingest_disabled')}</p>
      {/if}
    </Card>

    <!-- Allowed Senders -->
    <Card>
      <SectionHeading icon={Users} title={$_('pages.settings.interfaces_allowed_senders')} />
      <p class="text-xs text-text-muted mb-3">
        {$_('pages.settings.interfaces_allowed_senders_desc')}
      </p>

      {#if $userSettings.allowedIngestEmails.length > 0}
        <div class="flex flex-col gap-2 mb-4">
          {#each $userSettings.allowedIngestEmails as email (email)}
            <div class="flex justify-between items-center px-3 py-2 bg-bg-alt rounded">
              <span class="text-sm text-text font-mono">{email}</span>
              <Button
                variant="danger"
                size="sm"
                disabled={senderSaving}
                on:click={() => removeAllowedSender(email)}
              >
                <Trash2 class="w-3.5 h-3.5" strokeWidth={1.5} />
                {$_('pages.settings.interfaces_remove_email')}
              </Button>
            </div>
          {/each}
        </div>
      {:else}
        <p class="text-sm text-text-muted mb-4">{$_('pages.settings.interfaces_no_senders')}</p>
      {/if}

      <!-- Add Sender Form -->
      <div class="pt-3 border-t border-border">
        <div class="flex gap-2">
          <input
            type="email"
            bind:value={newSenderEmail}
            placeholder={$_('pages.settings.interfaces_add_email_placeholder')}
            class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
            on:keydown={(e) => e.key === 'Enter' && addAllowedSender()}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={senderSaving || !newSenderEmail.trim()}
            on:click={addAllowedSender}
          >
            <Plus class="w-4 h-4" strokeWidth={1.5} />
            {$_('pages.settings.interfaces_add_email')}
          </Button>
        </div>
      </div>

      {#if senderError}
        <div class="mt-3 px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error">
          {senderError}
        </div>
      {/if}
    </Card>
  {/if}

  <!-- Tab: Sharing / Freigabe (Spec 014 E8) -->
  {#if activeTab === 'sharing'}
    <Card>
      <SectionHeading icon={Share2} title={$_('pages.settings.share_title')} />
      <p class="text-xs text-text-muted mb-4">{$_('pages.settings.share_subtitle')}</p>

      {#if shareLinksLoading}
        <div class="flex items-center gap-2 text-sm text-text-muted">
          <Spinner size="sm" />
        </div>
      {:else if shareLinksError}
        <div class="px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error mb-3">
          {shareLinksError}
        </div>
        <Button variant="secondary" size="sm" on:click={loadShareLinks}>
          {$_('messages.retry')}
        </Button>
      {:else if shareLinks.length === 0}
        <p class="text-sm text-text-muted">{$_('pages.settings.share_empty')}</p>
      {:else}
        <table class="w-full table-fixed border-separate border-spacing-y-1 text-sm">
          <thead>
            <tr class="text-left text-xs font-medium text-text-muted">
              <th class="px-2 font-medium">{$_('pages.settings.share_document')}</th>
              <th class="hidden sm:table-cell w-36 px-2 font-medium"
                >{$_('pages.settings.share_visibility')}</th
              >
              <th class="hidden sm:table-cell w-24 px-2 font-medium"
                >{$_('pages.settings.share_expires')}</th
              >
              <th class="w-11"></th>
              <th class="w-11"></th>
            </tr>
          </thead>
          <tbody>
            {#each shareLinks as link (link.id)}
              <tr class="bg-bg-alt">
                <td class="rounded-l px-2 py-2">
                  <div class="truncate text-text" title={link.documentTitle}>
                    {link.documentTitle}
                  </div>
                </td>
                <td class="hidden sm:table-cell px-2 py-2">
                  <Badge variant="info"
                    >{link.visibility === 'public'
                      ? $_('pages.settings.share_visibility_public')
                      : $_('pages.settings.share_visibility_authenticated')}</Badge
                  >
                </td>
                <td class="hidden sm:table-cell px-2 py-2 text-text-muted text-xs">
                  {formatDate(String(link.expiresAt), $userSettings)}
                </td>
                <td class="py-1">
                  <CopyIconButton
                    copied={copiedShareLinkId === link.id}
                    label={$_('pages.settings.share_copy')}
                    copiedLabel={$_('pages.settings.share_copied')}
                    on:click={() => handleCopyShareLink(link.token, link.id)}
                  />
                </td>
                <td class="rounded-r py-1">
                  <Button
                    variant="danger"
                    size="sm"
                    iconOnly
                    aria-label={$_('pages.settings.share_revoke')}
                    disabled={revokingShareLinkId === link.id}
                    on:click={() => handleRevokeShareLink(link.documentId, link.id)}
                  >
                    {#if revokingShareLinkId === link.id}
                      <Spinner size="sm" />
                    {:else}
                      <Trash2 class="w-4 h-4" strokeWidth={1.5} />
                    {/if}
                  </Button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </Card>
  {/if}

  <!-- Tab: Privacy / DSGVO -->
  {#if activeTab === 'privacy'}
    <Card>
      <SectionHeading icon={Download} title={$_('pages.settings.privacy_export_title')} />
      <p class="text-xs text-text-muted mb-3">{$_('pages.settings.privacy_export_desc')}</p>

      <Button variant="primary" disabled={exportLoading} on:click={handleExportData}>
        {#if exportLoading}
          <Spinner size="sm" />
          {$_('pages.settings.privacy_export_loading')}
        {:else}
          <Download class="w-4 h-4" strokeWidth={1.5} />
          {$_('pages.settings.privacy_export_button')}
        {/if}
      </Button>

      {#if exportError}
        <div class="mt-3 px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error">
          {exportError}
        </div>
      {/if}
    </Card>

    <Card>
      <SectionHeading
        icon={AlertTriangle}
        title={$_('pages.settings.privacy_delete_title')}
        tone="danger"
      />
      <p class="text-xs text-text-muted mb-3">{$_('pages.settings.privacy_delete_desc')}</p>

      <Button
        variant="danger"
        on:click={() => {
          showDeleteModal = true;
          deleteConfirmText = '';
        }}
      >
        <Trash2 class="w-4 h-4" strokeWidth={1.5} />
        {$_('pages.settings.privacy_delete_button')}
      </Button>
    </Card>

    <Card>
      <SectionHeading icon={Shield} title={$_('pages.settings.privacy_policy_title')} />
      <a href="/datenschutz" class="text-sm text-accent hover:underline">
        {$_('pages.settings.privacy_policy_link')}
      </a>
    </Card>
  {/if}
</div>

<!-- Delete Member Confirmation Modal -->
<Modal
  open={deleteMemberTarget !== null}
  title={$_('pages.settings.delete_member_title')}
  on:close={() => (deleteMemberTarget = null)}
>
  <p class="text-sm text-text-secondary mb-2">{$_('pages.settings.delete_member_confirm')}</p>
  {#if deleteMemberTarget}
    <p class="text-sm font-medium text-text">{deleteMemberTarget.name}</p>
  {/if}
  <svelte:fragment slot="footer">
    <Button variant="danger" disabled={membersLoading} on:click={handleRemoveMember}>
      {membersLoading ? $_('messages.deleting') : $_('pages.settings.delete_member_button')}
    </Button>
    <Button
      variant="secondary"
      disabled={membersLoading}
      on:click={() => (deleteMemberTarget = null)}
    >
      {$_('pages.settings.cancel')}
    </Button>
  </svelte:fragment>
</Modal>

<!-- Delete User Data Confirmation Modal -->
<Modal
  open={showDeleteModal}
  title={$_('pages.settings.privacy_delete_confirm_title')}
  on:close={() => {
    showDeleteModal = false;
    deleteConfirmText = '';
  }}
>
  <div class="flex flex-col gap-4">
    <div class="px-3 py-2 bg-error/10 border border-error/30 rounded">
      <p class="text-sm text-error font-medium">
        {$_('pages.settings.privacy_delete_confirm_text')}
      </p>
    </div>

    <label class="flex items-center gap-2">
      <input type="checkbox" bind:checked={includeHouseholds} class="rounded border-border" />
      <span class="text-sm text-text">{$_('pages.settings.privacy_delete_include_households')}</span
      >
    </label>

    <div>
      <label for="delete-confirm" class="block text-sm text-text-muted mb-1">
        {$_('pages.settings.privacy_delete_confirm_type')}
      </label>
      <input
        id="delete-confirm"
        type="text"
        bind:value={deleteConfirmText}
        placeholder={$_('pages.settings.privacy_delete_confirm_word')}
        class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-error focus:ring-1 focus:ring-error/30"
      />
    </div>

    {#if deleteError}
      <div class="px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error">
        {deleteError}
      </div>
    {/if}
  </div>

  <svelte:fragment slot="footer">
    <Button
      variant="danger"
      disabled={deleteLoading ||
        deleteConfirmText !== $_('pages.settings.privacy_delete_confirm_word')}
      on:click={handleDeleteData}
    >
      {#if deleteLoading}
        {$_('pages.settings.privacy_delete_loading')}
      {:else}
        {$_('pages.settings.privacy_delete_confirm_button')}
      {/if}
    </Button>
    <Button
      variant="secondary"
      disabled={deleteLoading}
      on:click={() => {
        showDeleteModal = false;
        deleteConfirmText = '';
      }}
    >
      {$_('pages.settings.cancel')}
    </Button>
  </svelte:fragment>
</Modal>
